import { isArray, round, slice, toInteger } from 'lodash';
import type { IPoint } from '@/version-types';

/**
 * 判断四个角是否一致
 *
 * @param a - A 角
 * @param b - B 角
 * @param c - C 角
 * @param d - D 角
 */
export function areRadiusEqual(a: number, b: number, c: number, d: number) {
    return new Set([a, b, c, d]).size === 1;
}

/**
 * 范围映射计算
 *
 * @param oldVal - 旧范围的值
 * @param oldRange - 旧范围
 * @param newRange - 新范围
 */
export function mapRange(oldVal, oldRange: number[], newRange: number[]) {
    const [oldMin, oldMax] = oldRange;
    const [newMin, newMax] = newRange;
    return ((oldVal - oldMin) * (newMax - newMin)) / (oldMax - oldMin) + newMin;
}

/**
 * 角度转弧度
 *
 * @param angle - 角度
 */
export const angleToRadian = (angle: number) => {
    return (Math.PI / 180) * angle;
};

/**
 * 弧度转角度
 *
 * @param radian - 弧度
 */
export const radianToAngle = (radian: number) => {
    return (180 / Math.PI) * radian;
};

/**
 * 格式化数值
 *
 * @param num - 待格式化的数值
 * @param fractionDigits - 可选；小数点位数；默认值为 `2`
 */
export function toFixedNumber(num: number, fractionDigits = 2) {
    return +num.toFixed(fractionDigits);
}

/**
 * 判断值是否为有限数值。
 *
 * @param value - 待判断的未知值
 * @returns 如果值是有限 number，返回 true
 */
export function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 判断值是否为大于 0 的有限数值。
 *
 * @param value - 待判断的未知值
 * @returns 如果值是正有限 number，返回 true
 */
export function isPositiveFiniteNumber(value: unknown): value is number {
    return isFiniteNumber(value) && value > 0;
}

/**
 * 判断值是否为大于等于 0 的有限数值。
 *
 * @param value - 待判断的未知值
 * @returns 如果值是非负有限 number，返回 true
 */
export function isNonNegativeFiniteNumber(value: unknown): value is number {
    return isFiniteNumber(value) && value >= 0;
}

/**
 * 将数值向上取整并限制为非负结果。
 *
 * @param value - 需要处理的数值
 * @returns 大于等于 0 的向上取整结果
 */
export function ceilNonNegative(value: number) {
    return Math.max(0, Math.ceil(value));
}

/**
 * 根据 Map 索引，向数组索引位置插入对应值
 *
 * @example
 * ```js
 * let arr = [3];
 * let map = {0:1,2:2};
 * insertWithIndex(arr, map); // 结果: arr = [1,3,2];
 * ```
 *
 * @param arr - 数组
 * @param data - 数据
 */
export const insertWithIndex = <T>(arr: T[], data: Map<number, T>) => {
    if (!data.size) return;
    const len = arr.length;
    arr.length = len + data.size;
    const swapQueue: T[] = [];
    for (let i = 0; i < arr.length; i++) {
        if (data.has(i)) {
            const item = arr[i];
            if (item !== undefined) {
                swapQueue.push(item);
            }
            arr[i] = data.get(i) as T;
        } else {
            if (swapQueue.length) {
                const item = arr[i];
                if (item !== undefined) {
                    swapQueue.push(item);
                }
                arr[i] = swapQueue.shift() as T;
            }
        }
    }
};

/**
 * 在指定位置插入一组数据
 *
 * @param arr - 数组
 * @param index - 数组序号
 * @param data - 数据
 */
export const insertToArrByIndex = <T>(arr: T[], index: number, data: T | T[], insertBefore = true) => {
    // const destIndex = insertBefore ? index : index + 1;
    // const before = slice(arr, 0, destIndex);
    // const after = slice(arr, destIndex);
    // return before.concat(isArray(data) ? data : [data]).concat(after);
    const destIndex = insertBefore ? index : index + 1;
    // const arrCopy = [...arr]; // Create a copy of the array to avoid mutating the original
    arr.splice(destIndex, 0, ...(isArray(data) ? data : [data]));
    return arr;
};

/**
 * 两点相加
 *
 * @param p1 - 点 1
 * @param p2 - 点 2
 */
export const addPoint = function (p1: IPoint, p2: IPoint): IPoint {
    return {
        x: p1.x + p2.x,
        y: p1.y + p2.y,
    };
};

export function getVectorInSystem(point: IPoint, angle: number) {
    const rotate = (angle / 360) * 2 * Math.PI;
    const cos = Math.cos(rotate);
    const reversedDeltaY = -point.y;
    const vectorX = [point.x * cos, point.x * Math.cos(rotate - 0.5 * Math.PI)];
    const vectorY = [reversedDeltaY * Math.cos(0.5 * Math.PI + rotate), reversedDeltaY * cos];

    return {
        x: vectorX[0] + vectorY[0],
        y: -(vectorX[1] + vectorY[1]),
    };
}

export function getDiagonalLength(width: number, height: number) {
    return Math.sqrt(width * width + height * height);
}

export function getVector(length: number, angle: number): IPoint {
    return {
        x: length * Math.cos((angle / 180) * Math.PI),
        y: length * Math.sin((angle / 180) * Math.PI),
    };
}

export function getCenterFromLeftTop({
    leftTop,
    width,
    height,
    rotate,
}: {
    leftTop: IPoint;
    width: number;
    height: number;
    rotate: number;
}) {
    const length = getDiagonalLength(width, height) / 2;
    const angle1 = rotate;
    const angle2 = (Math.atan(height / width) / Math.PI) * 180;
    const angle = angle1 + angle2;
    return addPoint(leftTop, getVector(length, angle));
}

/**
 * 两点相减
 *
 * @param p1 - 点 1
 * @param p2 - 点 2
 */
export const subPoint = function (p1: IPoint, p2: IPoint): IPoint {
    return {
        x: p1.x - p2.x,
        y: p1.y - p2.y,
    };
};

/**
 * 一个点，沿着某个角度，移动一段距离，计算新的坐标
 *
 * @param point - 原始点
 * @param angleDegrees - 角度
 * @param distance - 移动的距离
 */
export function calculateNewCoordinates(point: IPoint, angleDegrees: number, distance: number) {
    // 将角度转换为弧度
    const angleRadians = angleDegrees * (Math.PI / 180);
    // 计算新的坐标
    const x1 = point.x + -distance * Math.cos(angleRadians);
    const y1 = point.y + -distance * Math.sin(angleRadians);

    return { x: x1, y: y1 };
}

/**
 * 两个点求向量
 *
 * @param p1 - 起始点
 * @param p2 - 终点
 */
export const twoPointToVector = function (p1: IPoint, p2: IPoint): IPoint {
    return {
        x: p2.x - p1.x,
        y: p2.y - p1.y,
    };
};

/**
 * 翻转 Y 轴方向
 *
 * @param point - 坐标点
 * @returns 翻转后的点
 */
export const translateYReverse = function (point: IPoint) {
    return {
        x: point.x,
        y: -point.y,
    };
};

/**
 * 一个点围绕另外一个点，顺时针旋转a角度后的新的坐标
 *
 * @param point - 坐标点
 * @param basePoint - 基准点
 * @param angle - 要绕 `basePoint` 顺时针旋转的角度
 */
export const rotatePointWithFocus = function (point: IPoint, basePoint: IPoint, angle: number) {
    const { x, y } = point;
    const { x: rx0, y: ry0 } = basePoint;
    const degree = (angle * Math.PI) / 180;
    const x0 = (x - rx0) * Math.cos(degree) - (y - ry0) * Math.sin(degree) + rx0;
    const y0 = (x - rx0) * Math.sin(degree) + (y - ry0) * Math.cos(degree) + ry0;

    return {
        x: x0,
        y: y0,
    };
};

/**
 * 旋转点
 *
 * @param point - 坐标点
 * @param rotate - 旋转角度
 */
export const rotatePoint = function (point: IPoint, rotate: number): IPoint {
    const { x, y } = point;

    const s = Math.sin((rotate * Math.PI) / 180);
    const c = Math.cos((rotate * Math.PI) / 180);

    return {
        x: x * c - y * s,
        y: x * s + y * c,
    };
};

/**
 * 翻转点
 *
 * @param point - 坐标点
 * @param flipH - 是否水平翻转
 * @param flipV - 是否垂直翻转
 */
export const flipPoint = (point: IPoint, flipH: boolean, flipV: boolean) => {
    const result: IPoint = {
        x: (flipH ? -1 : +1) * point.x,
        y: (flipV ? -1 : +1) * point.y,
    };
    return result;
};

/**
 * 不同坐标系转化  旋转
 *
 * @param point - 坐标点
 * @param angle - 要绕原点顺时针旋转一个图形角度
 */
export const translatePointAngle = rotatePoint;

/**
 * 获取实际角度
 *
 * - 例：`（361° = 1°）（-10° = 350°）`
 *
 * @param angle - 当前角度
 * @param rotationAngle - 旋转坐标轴角度
 * @returns number 正数
 */
export const getActualAngle = (angle: number, rotationAngle: number = 0) => {
    let newRotate = (angle + rotationAngle) % 360;
    if (newRotate < 0) {
        newRotate += 360;
    }
    return newRotate;
};

/**
 * 两个点之间的距离
 *
 * @param point1 - 点 1
 * @param point2 - 点 2
 */
export const pointsDistance = function (point1: IPoint, point2: IPoint) {
    return Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2));
};

/**
 * 两个向量的夹角
 *
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 * @param origin - 源点
 */
export const degreeBetweenPoints = (startPoint: IPoint, endPoint: IPoint, origin: IPoint = { x: 0, y: 0 }): number => {
    const x1 = startPoint.x - origin.x;
    const y1 = startPoint.y - origin.y;
    const x2 = endPoint.x - origin.x;
    const y2 = endPoint.y - origin.y;
    const cos = (x1 * x2 + y1 * y2) / (Math.sqrt(x1 * x1 + y1 * y1) * Math.sqrt(x2 * x2 + y2 * y2));

    const direction = x1 * y2 - y1 * x2; // 向量叉乘
    const degree = Math.acos(cos) || 0;

    return direction < 0 ? -degree : degree; // 叉乘结果为负表示逆时针旋转， 逆时针旋转减度数
};

/**
 * 两个向量求夹角
 *
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 */
export const getPointsBetweenAngle = (startPoint: IPoint, endPoint: IPoint): number => {
    const { x: x1, y: y1 } = startPoint;
    const { x: x2, y: y2 } = endPoint;

    if (x1 * x1 + y1 * y1 === 0 || x2 * x2 + y2 * y2 === 0) {
        return 0;
    }

    const cos = (x1 * x2 + y1 * y2) / (Math.sqrt(x1 * x1 + y1 * y1) * Math.sqrt(x2 * x2 + y2 * y2));

    const direction = x1 * y2 - y1 * x2; // 向量叉乘
    let degree = Math.acos(cos);
    degree = direction < 0 ? -degree : degree; // 叉乘结果为负表示逆时针旋转， 逆时针旋转减度数
    return (degree * 180) / Math.PI;
};

/**
 * 获取旋转方向 是顺时针还是逆时针
 *
 * @param vectorA - 向量 A
 * @param vectorB - 向量 B
 */
export const getRotationDirection = (vectorA: IPoint, vectorB: IPoint): number => {
    const { x: x1, y: y1 } = vectorA;
    const { x: x2, y: y2 } = vectorB;

    // 计算叉积
    const crossProduct = x1 * y2 - x2 * y1;

    if (crossProduct > 0) {
        return -1; // 逆时针
    } else if (crossProduct < 0) {
        return 1; // 顺时针
    } else {
        return 0; // 共线
    }
};

/**
 * 根据一个点跟长度，求新的点坐标
 *
 * @param currentPoint - 当前点
 * @param length - 长度
 * @param originPoint - 中心点
 */
export function getNewPointByLength(currentPoint: IPoint, length: number, originPoint = { x: 0, y: 0 }): IPoint {
    const origin = [originPoint.x, originPoint.y];
    const point = [currentPoint.x, currentPoint.y];
    const dx = point[0] - origin[0];
    const dy = point[1] - origin[1];
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance === 0) {
        return {
            x: 0,
            y: 0,
        };
    }
    const unitVectorX = dx / distance;
    const unitVectorY = dy / distance;
    const newPointX = origin[0] + unitVectorX * length;
    const newPointY = origin[1] + unitVectorY * length;
    return { x: Math.ceil(newPointX), y: Math.ceil(newPointY) };
}

// /** 两个坐标求夹角 */
// export function getPointsAngle(startPoint: IPoint, endPoint: IPoint): number {
//     const { x: x1, y: y1 } = startPoint;
//     const { x: x2, y: y2 } = endPoint;

//     const deltaY = y2 - y1;
//     const deltaX = x2 - x1;
//     const angle = Math.atan2(deltaY, deltaX);
//     console.log(x1, y1, x2, y2, angle);

//     return angle * (180 / Math.PI);
// }

/**
 * 角度和长边求 X、Y 坐标
 *
 * @param angle - 角度
 * @param distance - 距离
 */
export const translateAngleToXY = function (angle: number, distance: number) {
    return {
        x: distance * Math.cos((angle * Math.PI) / 180),
        y: -distance * Math.sin((angle * Math.PI) / 180),
    };
};

/**
 * 通过长短边求夹角
 *
 * @param x - 水平坐标
 * @param y - 垂直坐标
 */
export const translateXYToAngle = function (x: number, y: number) {
    const angle = Math.atan2(-y, x);

    return (angle * 180) / Math.PI;
};

/**
 * 短边求长边
 *
 * @param x - 水平坐标
 * @param y - 垂直坐标
 */
export const translateLongSide = function (x: number, y: number) {
    return Math.sqrt(x * x + y * y);
};

/**
 * 坐标转化为整数类型
 *
 * @param point - 转化的点
 */
export const transferPointToInt = function (point: IPoint) {
    const result: IPoint = {
        x: toInteger(point.x),
        y: toInteger(point.y),
    };

    return result;
};

/**
 * 坐标转化 移动坐标
 *
 * @param point - 转化的点
 * @param moveX - 移动的 X 坐标
 * @param moveY - 移动的 Y 坐标
 */
export const translatePointMove = function (point: IPoint, moveX: number = 0, moveY: number = 0) {
    const result: IPoint = {
        x: point.x + moveX,
        y: point.y + moveY,
    };

    return result;
};

/**
 * Y 坐标转成负的
 *
 * @param point - 转化的点
 */
export const revertPointY = function (point: IPoint) {
    const result: IPoint = {
        x: point.x,
        y: -point.y,
    };

    return result;
};

/**
 * 两个点的相对位置
 *
 * @param point1 - 坐标点 1
 * @param point2 - 坐标点 2
 */
export const pointRelation = function (point1: IPoint, point2: IPoint) {
    const result = {
        x: point2.x - point1.x,
        y: point2.y - point1.y,
    };

    return result;
};

/**
 * 缩放点的位置
 *
 * @param point - 坐标点
 * @param scale - 缩放比例
 */
export const pointScale = function (point: IPoint, scale: number = 1) {
    const result: IPoint = {
        x: point.x * scale,
        y: point.y * scale,
    };

    return result;
};

/**
 * 判断两条定长的线段是否相交，求两条线的交点，然后求交点是否在定长线段上
 *
 * @see https://zhuanlan.zhihu.com/p/158533421
 * @param a - 坐标 A
 * @param b - 坐标 B
 * @param c - 坐标 C
 * @param d - 坐标 D
 */
export const hasTwoLineIntersect = (a: IPoint, b: IPoint, c: IPoint, d: IPoint) => {
    /** 1 解线性方程组, 求线段交点. **/
    // 如果分母为0 则平行或共线, 不相交
    const denominator = (b.y - a.y) * (d.x - c.x) - (a.x - b.x) * (c.y - d.y);
    if (denominator === 0) {
        return false;
    }

    // 线段所在直线的交点坐标 (x , y)
    const x =
        ((b.x - a.x) * (d.x - c.x) * (c.y - a.y) + (b.y - a.y) * (d.x - c.x) * a.x - (d.y - c.y) * (b.x - a.x) * c.x) /
        denominator;
    const y =
        -((b.y - a.y) * (d.y - c.y) * (c.x - a.x) + (b.x - a.x) * (d.y - c.y) * a.y - (d.x - c.x) * (b.y - a.y) * c.y) /
        denominator;

    /** 2 判断交点是否在两条线段上 **/
    if (
        // 交点在线段1上
        (x - a.x) * (x - b.x) <= 0 &&
        (y - a.y) * (y - b.y) <= 0 &&
        // 且交点也在线段2上
        (x - c.x) * (x - d.x) <= 0 &&
        (y - c.y) * (y - d.y) <= 0
    ) {
        // 返回交点p
        return true;
    }
    //否则不相交
    return false;
};

/**
 * 判断点是否在线上
 *
 * @param point - 坐标点
 * @param line - 线的坐标
 */
export const isPointInLine = (
    point: IPoint,
    line: {
        startPoint: IPoint;
        endPoint: IPoint;
    },
) => {
    const { startPoint, endPoint } = line;
    return (
        (endPoint.x - startPoint.x) * (point.y - startPoint.y) - (point.x - startPoint.x) * (endPoint.y - startPoint.y)
    );
};

/**
 * 判断点是否在矩形里面
 *
 * @param point - 坐标点
 * @param rect - 矩形坐标
 */
export const isPointInRect = (
    point: IPoint = { x: 886, y: 244 },
    rect: {
        leftTopPoint: IPoint;
        rightTopPoint: IPoint;
        leftBottomPoint: IPoint;
        rightBottomPoint: IPoint;
    } = {
        leftTopPoint: { x: 1224, y: 301 },
        rightTopPoint: { x: 1524, y: 301 },
        leftBottomPoint: { x: 1224, y: 601 },
        rightBottomPoint: { x: 1524, y: 601 },
    },
) => {
    const { leftTopPoint, rightTopPoint, leftBottomPoint, rightBottomPoint } = rect;
    const isPointIn =
        isPointInLine(point, {
            startPoint: leftTopPoint,
            endPoint: rightTopPoint,
        }) *
            isPointInLine(point, {
                startPoint: rightBottomPoint,
                endPoint: leftBottomPoint,
            }) >=
            0 &&
        isPointInLine(point, {
            startPoint: leftBottomPoint,
            endPoint: leftTopPoint,
        }) *
            isPointInLine(point, {
                startPoint: rightTopPoint,
                endPoint: rightBottomPoint,
            }) >=
            0;
    return isPointIn;
};

/**
 * 保留两位小数
 *
 * @param value - 数值
 */
export const toFixNumber = (value: number) => {
    return round(value, 2);
};

/**
 * 转为整数值
 *
 * @param value - 数值
 */
export const transferToInt = (value: number) => {
    return round(value, 0);
};

export const transferXYToAngle = (x: number, y: number) => {
    if (x === 0 && y === 0) {
        return 0;
    }
    let angle = translateXYToAngle(x, y);

    return (angle = +angle.toFixed(1));
};

export const transferXYToDistance = (x: number, y: number) => {
    let distance = translateLongSide(x, y);
    distance = +distance.toFixed(1);
    return distance;
};

/**
 * 获取直线上的整数点整数点
 *
 * @param x1 - 坐标点 1
 * @param y1 - 坐标点 1
 * @param x2 - 坐标点 2
 * @param y2 - 坐标点 2
 */
export function getLineIntegerPoints(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;

    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b)); // 计算最大公约数
    const divisor = gcd(Math.abs(dx), Math.abs(dy)); // 斜率的分母
    const a = dy / divisor; // 斜率的分子
    const b = dx / divisor; // 斜率的分母
    if (isNaN(a) || isNaN(b)) {
        return [
            {
                x: x1,
                y: y1,
            },
        ];
    }

    const points = [] as IPoint[];
    for (let t = 0; t <= divisor; t++) {
        const x = x1 + b * t;
        const y = y1 + a * t;
        points.push({ x, y });
    }

    return points;
}

/**
 * 判断点是否在矩形外面
 *
 * @param points - 坐标点
 * @param cornerPositions - 矩形坐标
 */
export const pointsOutOfRangeRectangle = (
    points: IPoint[],
    cornerPositions: { leftTop: IPoint; rightTop: IPoint; leftBottom: IPoint; rightBottom: IPoint },
) => {
    const minX = cornerPositions.leftTop.x;
    const maxX = cornerPositions.rightBottom.x;
    const minY = cornerPositions.rightBottom.y;
    const maxY = cornerPositions.leftTop.y;
    for (let i = 0; i < points.length; i++) {
        const point = points[i];
        if (
            Math.trunc(point.x) < Math.trunc(minX) ||
            Math.trunc(point.x) > Math.trunc(maxX) ||
            Math.trunc(point.y) < Math.trunc(minY) ||
            Math.trunc(point.y) > Math.trunc(maxY)
        ) {
            return true;
        }
    }
    return false;
};

/**
 * 计算四个点组成的包围盒坐标
 * @param {Array} points - 点的数组，格式为 [[x1, y1], [x2, y2], [x3, y3], [x4, y4]]
 * @returns {Object} 包围盒坐标 { minX, minY, maxX, maxY }
 */
export function calculateBoundingBox(points: number[][]) {
    if (points.length !== 4) {
        throw new Error('请输入四个点');
    }

    // 提取所有 X 和 Y 坐标
    const xCoords = points.map((point) => point[0]);
    const yCoords = points.map((point) => point[1]);

    // 计算最小和最大坐标
    const minX = Math.min(...xCoords);
    const minY = Math.min(...yCoords);
    const maxX = Math.max(...xCoords);
    const maxY = Math.max(...yCoords);

    return { minX, minY, maxX, maxY };
}
